import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import GoogleMapReact from "google-map-react";
import ChargingStationsMapMarker from "../ChargingStationsMapMarker/ChargingStationsMapMarker";
import MapStyles from "./MapStyles";
import styles from "./BaseGoogleMapReact.module.scss";
import fullscreenOpenIcon from "../../../client_customizations/assets/images/icons/fullscreen-open.svg";
import fullscreenCloseIcon from "../../../client_customizations/assets/images/icons/fullscreen-close.svg";
import panToggleIcon from "../../../client_customizations/assets/images/icons/pan-toggle.svg";

const DEFAULT_CENTER = { lat: 37.7916858, lng: -122.397855 };
const INITIAL_ZOOM = 15;
const K_HOVER_DISTANCE = 30;
const API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY;

const BaseGoogleMapReact = ({
  center,
  children,
  onGoogleApiLoaded,
  onHoverOnStation,
  onHoverOffStation,
  onClickMarker,
  chargingStations,
  selectedStation,
  ...rest
}) => {
  const [map, setMap] = useState(null);
  const [controlsOpen, setControlsOpen] = useState(false);

   const [isFullscreen, setIsFullscreen] = useState(false);

   useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const hoverProps = {
    ...(onHoverOnStation && {
      onChildMouseEnter: (key, childProps) => {
        onHoverOnStation(childProps.station.id);
      },
    }),
    ...(onHoverOffStation && {
      onChildMouseLeave: onHoverOffStation,
    }),
  };

  const handleApiLoaded = (api) => {
    setMap(api.map);
    if (onGoogleApiLoaded) {
      onGoogleApiLoaded(api);
    }
  };
   const toggleFullscreen = () => {
     const element = document.querySelector(`.${styles.mapContainer}`);
     if (document.fullscreenElement) {
       document.exitFullscreen();
     } else {
       element.requestFullscreen().catch((err) => {
         console.error("Fullscreen error:", err);
       });
     }
   };


  return (
    <div className={styles.mapContainer}>
      <GoogleMapReact
        {...rest}
        bootstrapURLKeys={{
          key: API_KEY,
          libraries: "places",
        }}
        center={center || DEFAULT_CENTER}
        defaultZoom={INITIAL_ZOOM}
        options={{ styles: MapStyles, disableDefaultUI: true }}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={handleApiLoaded}
        hoverDistance={K_HOVER_DISTANCE}
        {...hoverProps}
      >
        {(chargingStations || []).map((station) => (
          <ChargingStationsMapMarker
            key={station.id}
            station={station}
            selectedStation={selectedStation}
            lat={station.latitude}
            lng={station.longitude}
            onClick={onClickMarker}
          />
        ))}
        {children}
      </GoogleMapReact>

      {map && (
        <>
          <div className={styles.mapControlsWrapper}>
            <button
              className={styles.diamondToggle}
              onClick={() => setControlsOpen(!controlsOpen)}
              title="Toggle map controls"
            >
              <img src={panToggleIcon} alt="Map controls" />
            </button>

            {controlsOpen && (
              <div className={styles.expandedControls}>
                <div className={styles.panRow}>
                  <button onClick={() => map.panBy(0, -100)}>↑</button>
                </div>
                <div className={styles.panRow}>
                  <button onClick={() => map.panBy(-100, 0)}>←</button>
                  <button onClick={() => map.panBy(100, 0)}>→</button>
                </div>
                <div className={styles.panRow}>
                  <button onClick={() => map.panBy(0, 100)}>↓</button>
                </div>
                <div className={styles.zoomRow}>
                  <button onClick={() => map.setZoom(map.getZoom() + 1)}>
                    +
                  </button>
                  <button onClick={() => map.setZoom(map.getZoom() - 1)}>
                    −
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className={styles.fullscreenControl}>
            <button onClick={toggleFullscreen} title="Toggle fullscreen">
              <img
                src={isFullscreen ? fullscreenCloseIcon : fullscreenOpenIcon}
                alt={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

BaseGoogleMapReact.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  children: PropTypes.node,
  onClickMarker: PropTypes.func,
  onHoverOnStation: PropTypes.func,
  onHoverOffStation: PropTypes.func,
  chargingStations: PropTypes.array,
  selectedStation: PropTypes.object,
};

BaseGoogleMapReact.defaultProps = {
  chargingStations: [],
  center: DEFAULT_CENTER,
  onClickMarker: () => {},
};

export default BaseGoogleMapReact;
